import { withResolvers } from "@otto-ec/assets-core-utils/promise";
import { on, unsetAll, type Unset } from "../../event/head";
import { nanoid } from "../../misc/head";
import type { TargetStore } from "../store/store.types";
import type { dispatchSignalInternal } from "./private";
import { win } from "../../../common/namespaces";

export interface MessageBase {
  /*                                                                                */
  messageId: string;
  /*                                               */
  path: string;
}

/**
 *
 */
export interface MessageAsk extends MessageBase {
  /*                                                                    */
  type: "SIGNAL_ASK";
  /*                                             */
  detail: unknown;
  /*                                                  */
  retain?: boolean | number;
}

/**
 *
 */
export interface MessageSyn extends MessageBase {
  /*                                                                         */
  type: "SIGNAL_SYN";
}

export type MessageRetVal = [undefined, unknown] | [unknown, undefined];

/**
 *
 */
export interface MessageAck extends MessageBase {
  /*                                                                           */
  type: "SIGNAL_ACK";
  /*                                               */
  retval: MessageRetVal[];
}

const messageTypes = ["SIGNAL_ASK", "SIGNAL_SYN", "SIGNAL_ACK"] as const satisfies (
  | MessageAck
  | MessageAsk
  | MessageSyn
)["type"][];

/**
 *
 */
export const createChannels = (): [
  send: BroadcastChannel | null,
  recv: BroadcastChannel | null,
] => {
  /*                                                      */
  if (import.meta.env.SSR) return [null, null];
  const BC = win.BroadcastChannel;

  /*                                                                                   */
  if (typeof BC !== "function") return [null, null];

  /*                                 */
  return ["send", "recv"].map((name) => new BC(`otto-nexus-${name}`)) as never;
};

/**
 *
 *
 *
 */
const [send, recv] = createChannels();

/**
 *
 */
type PendingEntry = [
  timeout: number,
  resolve: (value: MessageRetVal[]) => void,
  results: (MessageRetVal[] | null)[],
];

/**
 *
 *
 */
const pending = new Map<string, PendingEntry>();

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const handleSend = async (
  store: TargetStore,
  recvCh: BroadcastChannel,
  dispatch: typeof dispatchSignalInternal,
  event: MessageEvent<MessageAck | MessageSyn | MessageAsk>,
): Promise<void> => {
  /*                                                  */
  const { path: eventPath, messageId } = event.data;
  const { path, log } = store;
  const [MSG_ASK, MSG_SYN, MSG_ACK] = messageTypes;

  log.debug("Incomming message:", event.data.type, "id:", messageId);
  if (eventPath !== path) return log.debug("Ignore message for path:", eventPath);
  if (event.data.type !== MSG_ASK) return log.debug("Ignore message type:", event.data.type);

  /*                                                                                                       */
  recvCh.postMessage({ type: MSG_SYN, path, messageId });
  /*                                                    */
  const [, retval] = dispatch(store, [event.data.detail, { retain: event.data.retain }]);

  /*                             */
  const payload: MessageRetVal[] = await Promise.all(
    retval.map(async (result) => {
      try {
        return [undefined, await result];
      } catch (error) {
        return [error, undefined];
      }
    }),
  );

  /*                                   */
  log.debug("Sending results for message:", messageId, payload);
  return recvCh.postMessage({
    type: MSG_ACK,
    messageId,
    path,
    retval: payload,
  } satisfies MessageAck);
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const handleRecv = (
  store: TargetStore,
  event: MessageEvent<MessageAck | MessageSyn | MessageAsk>,
): void => {
  /*                                                  */
  const { path: eventPath, messageId } = event.data;
  const { path, log } = store;
  const [, MSG_SYN, MSG_ACK] = messageTypes;

  log.debug("Returning message:", event.data.type, "id:", messageId);
  if (eventPath !== path) return log.debug("Ignore message for path:", eventPath);

  /*                                                        */
  if (event.data.type === MSG_SYN) {
    const pendingEntry = pending.get(event.data.messageId);
    /*                                                 */
    if (!pendingEntry) return;

    /*                                                                                                                       */
    window.clearTimeout(pendingEntry[0]);

    /*                                                            */
    log.debug("Add pending entry for message:", messageId);
    pendingEntry[2].push(null);
  }

  /*                                          */
  if (event.data.type === MSG_ACK) {
    const pendingEntry = pending.get(event.data.messageId);
    /*                                                 */
    if (!pendingEntry) return;

    const retvals = pendingEntry[2];

    /*                                        */
    log.debug("Add results for message:", messageId, event.data.retval);
    const nullIndex = retvals.indexOf(null);
    if (nullIndex !== -1) {
      retvals.splice(nullIndex, 1);
    }
    /*                             */
    retvals.push(event.data.retval);

    if (retvals.includes(null)) return;

    /*                                                                         */
    log.debug("Resolving pending entry for message:", messageId, retvals);
    pendingEntry[1]((retvals as MessageRetVal[][]).flat(1));
    pending.delete(event.data.messageId);
  }
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const onMessageIfGlobal = (
  store: TargetStore,
  dispatch: typeof dispatchSignalInternal,
): Unset | undefined => {
  if (import.meta.env.SSR || !send || !recv || !store.broadcast) return;

  store.log.debug("Init broadcast channel for path:", store.path);
  return unsetAll(
    on(send, "message", (event) => handleSend(store, recv, dispatch, event)),
    on(recv, "message", (event) => handleRecv(store, event)),
  );
};

/**
 *
 *
 *
 *
 *
 *
 */
export const broadcastIfGlobal = (store: TargetStore): Promise<unknown> | undefined => {
  if (import.meta.env.SSR || !send || !store.broadcast) return;

  const messageId = nanoid();
  const { path, detail, retained } = store;
  const [MSG_ASK] = messageTypes;

  /*                                                         */
  const [result, resolve] = withResolvers<unknown>();

  /*                                                                                          */
  /*                                                                                                 */
  const timeout = window.setTimeout(() => {
    store.log.debug(
      "No response received for broadcast message, resolving with empty array. Message ID:",
      messageId,
    );
    pending.delete(messageId);
    resolve([]);
  }, 100);

  /*                                                                                                */
  /*                                    */
  /*                                                                                    */
  /*                                                       */
  pending.set(messageId, [timeout, resolve, []]);

  /*                                 */
  send.postMessage({ type: MSG_ASK, messageId, path, detail, retain: retained });

  return result;
};
